import { useState, useMemo, useCallback, useEffect } from 'react';

interface Options {
  totalItems: number;
  pageSize: number;
  initialPage?: number;
  /** 외부에서 totalItems가 줄어들었을 때 currentPage를 last page로 clamp할지 */
  clampOnTotalChange?: boolean;
}

export const usePagination = ({
  totalItems,
  pageSize,
  initialPage = 1,
  clampOnTotalChange = true,
}: Options) => {
  const [currentPage, setCurrentPage] = useState(initialPage);

  const totalPages = useMemo(() => {
    if (pageSize <= 0) return 0;
    return Math.max(1, Math.ceil(totalItems / pageSize));
  }, [totalItems, pageSize]);

  // totalItems 줄었을 때 currentPage가 totalPages를 넘으면 clamp
  useEffect(() => {
    if (!clampOnTotalChange) return;
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages, clampOnTotalChange]);

  const goPrev = useCallback(() => {
    setCurrentPage(p => Math.max(1, p - 1));
  }, []);
  const goNext = useCallback(() => {
    setCurrentPage(p => Math.min(totalPages, p + 1));
  }, [totalPages]);

  const reset = useCallback(() => setCurrentPage(initialPage), [initialPage]);

  const sliceItems = useCallback(
    <T,>(items: T[]): T[] => {
      const start = (currentPage - 1) * pageSize;
      return items.slice(start, start + pageSize);
    },
    [currentPage, pageSize]
  );

  return {
    currentPage,
    setCurrentPage,
    totalPages,
    hasPrev: currentPage > 1,
    hasNext: currentPage < totalPages,
    goPrev,
    goNext,
    reset,
    sliceItems,
  };
};
